import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Row, Col, Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { TypedMemo } from '../common/types';
import { SMLContainer } from '../smartLinks/smlContainer/SMLContainer';
import { SMLRouteNames, SMLRoutes } from '../../routing/sml/SMLRoutes';
import { useSMLRouter } from '../../routing/sml/hooks';
import { SMLDatePicker } from './components/SMLDatePicker/SMLDatePicker';
import { PlaylistSearchField, MeiliPlaylistData } from './components/SearchField';
import { TopPlaylistsByFollowersCard } from './components/TopPlaylistsByFollowersCard/TopPlaylistsByFollowersCard';
import { TrendingPlaylistsCard } from './components/TrendingPlaylistsCard/TrendingPlaylistsCard';
import { TrackPlaylistModal } from './components/TrackPlaylistModal/TrackPlaylistModal';
import { ChartsPageHead } from './charts/ChartsPageHead';
import { PageTabsInBody } from './components/SMLPageHead/PageTabsInBody';
import { ChartsTabRow } from './charts/ChartsTabRow';
import { chartsTabLeadMessages } from './charts/chartsMessages';

const SMLPlaylistsHomeComponent: React.FC = () => {
  const intl = useIntl();
  const router = useSMLRouter();

  const [isTrackModalOpen, setIsTrackModalOpen] = useState(false);

  const handlePlaylistSelected = useCallback(
    (playlist: MeiliPlaylistData) => {
      router.push(SMLRoutes.playlistDetails, { playlistId: playlist.id });
    },
    [router]
  );

  const handleTrackSuccess = useCallback(
    (playlistId: string) => {
      setIsTrackModalOpen(false);
      router.push(SMLRoutes.playlistDetails, { playlistId });
    },
    [router]
  );

  return (
    <SMLContainer
      header={
        <ChartsPageHead tab={SMLRouteNames.playlists}>
          <SMLDatePicker />
        </ChartsPageHead>
      }
    >
      <PageTabsInBody>
        <ChartsTabRow screen={SMLRouteNames.playlists} />
      </PageTabsInBody>
      <p className="text-body-secondary mb-3">{intl.formatMessage(chartsTabLeadMessages.playlists)}</p>
      <Row className="mb-4 align-items-center">
        <Col xs={12} md={6} lg={5}>
          <PlaylistSearchField
            placeholder={intl.formatMessage({
              id: 'app.search.playlists.placeholder',
              defaultMessage: 'Search for playlists...',
            })}
            onPlaylistSelected={handlePlaylistSelected}
          />
        </Col>
        <Col xs="auto">
          <Button color="primary" onClick={() => setIsTrackModalOpen(true)}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            {intl.formatMessage({
              id: 'app.sml.playlists.trackByUrl.button',
              defaultMessage: 'Track Playlist',
            })}
          </Button>
        </Col>
      </Row>

      <Row>
        <Col xs={12} xl={6} className="pb-4">
          <TopPlaylistsByFollowersCard />
        </Col>
        <Col xs={12} xl={6} className="pb-4">
          <TrendingPlaylistsCard />
        </Col>
      </Row>
      <TrackPlaylistModal
        isOpen={isTrackModalOpen}
        onClose={() => setIsTrackModalOpen(false)}
        onSuccess={handleTrackSuccess}
      />
    </SMLContainer>
  );
};

export const SMLPlaylistsHome = TypedMemo(SMLPlaylistsHomeComponent);
